import { useState, useEffect, useRef, useContext } from 'react';
import Image from 'next/image';
import { Box, Skeleton, useTheme } from '@mui/material';
import { Context } from '@/context';

const CustomImageNoticiasComponent = ({ src, alt = 'Imagen', priority = false, unoptimized = true }) => {
    const defaultImage = `${process.env.NEXT_PUBLIC_IMAGES}/2024/secciones/alineaciones-probables.jpg`;
    const [imageSrc, setImageSrc] = useState(null);
    const [isVisible, setIsVisible] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const imageRef = useRef(null);

    const theme = useTheme();
    const { state } = useContext(Context);
    const isDarkMode = state?.theme === 'dark' || !state;

    useEffect(() => {
        setImageSrc(src || defaultImage);
    }, [src]);

    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        setIsVisible(true);
                        observer.unobserve(entry.target);
                    }
                });
            },
            {
                root: null,
                rootMargin: '50px',
                threshold: 0.1,
            }
        );

        if (imageRef.current) {
            observer.observe(imageRef.current);
        }

        return () => {
            if (imageRef.current) {
                observer.unobserve(imageRef.current);
            }
        };
    }, []);

    const handleError = () => {
        console.warn(`⚠️ Error cargando la imagen: ${src}`);
        setImageSrc(defaultImage);
    };

    const handleLoad = () => {
        setIsLoading(false);
    };

    return (
        <Box
            ref={imageRef}
            position="relative"
            width="100%"
            sx={{
                aspectRatio: '16 / 9',
                bgcolor: isDarkMode ? 'background.default' : 'grey.100',
                border: `1px solid ${theme.palette.border.main}`,
            }}
        >
            {isLoading && (
                <Skeleton
                    variant="rectangular"
                    width="100%"
                    height="100%"
                    animation="wave"
                    sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        bgcolor: isDarkMode ? 'background.default' : 'grey.200',
                    }}
                />
            )}
            {isVisible && imageSrc && (
                <Image
                    src={imageSrc}
                    alt={alt}
                    fill
                    priority={priority}
                    unoptimized={unoptimized}
                    sizes="(max-width: 992px) 770px, (min-width: 993px) 770px"
                    onError={handleError}
                    onLoad={handleLoad}
                    style={{
                        objectFit: 'cover',
                        opacity: isLoading ? 0 : 1,
                        transition: 'opacity 0.3s ease-in-out',
                    }}
                />
            )}
        </Box>
    );
};

export default CustomImageNoticiasComponent;
